import fmt_date, { format_datetime_dd_mmm_yy as fmt_datetime } from '../../../utils/format_date_dd_mmm_yy';
import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Card, CardBody } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import reformat_prose_dates from '../../../utils/reformat_prose_dates';

const MODULES = ['DESIGN', 'RENDER', 'PRESENTATION', 'CATALOGUE', 'PRICING', 'PRODUCTION'];

// Bold the first sentence of a summary as a scannable lead-in, keep the rest
// as normal weight. No-op if there's no early sentence break.
function render_lead(text) {
    if (!text) return null;
    const idx = text.indexOf('. ');
    if (idx === -1 || idx > 160) return text;
    return (
        <>
            <strong>{text.slice(0, idx + 1)}</strong>
            {text.slice(idx + 1)}
        </>
    );
}

const STATUS_COLOR = {
    HEALTHY: '#1e8449',
    HEALTHY_WITH_CAVEATS: '#b7950b',
    ISSUES_PRESENT: '#c0392b',
    UNOBSERVED: '#7f8c8d',
    NOT_IN_SCOPE: '#95a5a6',
    UNKNOWN: '#34495e',
};

const TREND_SYMBOL = {
    improving: '▲',
    stable: '→',
    declining: '▼',
    unknown: '·',
};

const TREND_COLOR = {
    improving: '#1e8449',
    stable: '#7f8c8d',
    declining: '#c0392b',
    unknown: '#7f8c8d',
};

// Account/billing status colors. Green = healthy paying; amber = attention
// (renewal/payment/grace); red = suspended/churned; grey = trial/unknown.
// Mirrors the server-side computeAccountStatus classification.
const ACCOUNT_STATUS_COLOR = {
    active: '#1e8449',
    renewal_due_soon: '#b7950b',
    payment_due: '#e67e22',
    grace_period: '#e67e22',
    suspended: '#c0392b',
    churned: '#c0392b',
    trial: '#7f8c8d',
    unknown: '#95a5a6',
};

// Compact tooltip for the account-status badge — the note (with its deadline,
// e.g. "suspends ~2026-07-18 if unpaid") plus the billing-cycle anchors, so a
// rep can see WHY and by-when without leaving the page.
function account_status_title(a) {
    if (!a) return '';
    const bits = [];
    if (a.note) bits.push(a.note);
    if (a.lastBillingDate) bits.push(`Last paid cycle from: ${fmt_date(a.lastBillingDate)}`);
    if (a.nextBillingDate) bits.push(`Next billing: ${fmt_date(a.nextBillingDate)}`);
    return bits.join('  ·  ') || 'No billing dates on record';
}

const Adoption = ({ store_id, set_page_loader }) => {
    const [data, set_data] = useState(null);
    const [err, set_err] = useState(null);
    const [busy, set_busy] = useState(false);

    const load = useCallback(async () => {
        set_err(null);
        try {
            const resp = await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/health`,
            });
            set_data(resp);
        } catch (e) {
            set_err(e.message || String(e));
        }
    }, [store_id]);

    useEffect(() => {
        load();
    }, [load]);

    const trigger_reanalyze = async () => {
        set_busy(true);
        try {
            await cs_analysis_fetch({
                path: `/api/v1/clients/${encodeURIComponent(store_id)}/analyses`,
                method: 'POST',
                body: { triggerDetail: 'manual from CSP UI' },
            });
            // Pipeline is async — give the worker a moment then refresh.
            setTimeout(load, 1500);
        } catch (e) {
            set_err(e.message || String(e));
        } finally {
            set_busy(false);
        }
    };

    if (err) {
        return (
            <Card>
                <CardBody>
                    <div style={{ color: '#c0392b' }}>cs-analysis unreachable: {err}</div>
                    <Button size='sm' color='primary' onClick={load} style={{ marginTop: 12 }}>
                        Retry
                    </Button>
                </CardBody>
            </Card>
        );
    }

    if (!data) {
        return (
            <Card>
                <CardBody>Loading…</CardBody>
            </Card>
        );
    }

    const modules = data.modules || {};
    const openByModule = data.openFindingsByModule || {};

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Card>
                <CardBody style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>
                            {data.client.name}
                            <span style={{ fontWeight: 400, color: '#7f8c8d', marginLeft: 8 }}>
                                store {data.client.storeId}
                            </span>
                            {data.accountStatus ? (
                                <Badge
                                    title={account_status_title(data.accountStatus)}
                                    style={{
                                        marginLeft: 8,
                                        fontWeight: 500,
                                        fontSize: 11,
                                        verticalAlign: 'middle',
                                        backgroundColor: ACCOUNT_STATUS_COLOR[data.accountStatus.status] || '#95a5a6',
                                        color: '#fff',
                                    }}
                                >
                                    {data.accountStatus.label}
                                </Badge>
                            ) : null}
                        </div>
                        {data.latestAnalysis ? (
                            <div style={{ fontSize: 13, color: '#34495e', marginTop: 6 }}>
                                Latest analysis: r{data.latestAnalysis.revision} on{' '}
                                {fmt_datetime(data.latestAnalysis.createdAt)} ·
                                trigger {data.latestAnalysis.trigger} ·
                                confidence{' '}
                                {data.latestAnalysis.confidence != null
                                    ? Math.round(data.latestAnalysis.confidence * 100) + '%'
                                    : 'n/a'}
                            </div>
                        ) : (
                            <div style={{ fontSize: 13, color: '#7f8c8d', marginTop: 6 }}>
                                No analysis yet — trigger a re-analyze below.
                            </div>
                        )}
                        {data.latestAnalysis && data.latestAnalysis.summary ? (
                            <div style={{ fontSize: 14, color: '#34495e', marginTop: 10, maxWidth: 900, lineHeight: 1.6 }}>
                                {render_lead(reformat_prose_dates(data.latestAnalysis.summary))}
                            </div>
                        ) : null}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button size='sm' onClick={load} disabled={busy}>
                            Refresh
                        </Button>
                        <Button size='sm' color='primary' onClick={trigger_reanalyze} disabled={busy}>
                            {busy ? 'Queuing…' : 'Re-analyze now'}
                        </Button>
                    </div>
                </CardBody>
            </Card>

            {/* 2-column grid for module cards. Previously 180px minmax which
                packed 6 cards in one row at desktop widths — too cramped to
                read the per-module summary + status + counts together. 360px
                minmax gives each card breathing room while keeping all 6
                modules visible without scrolling on a typical monitor. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12 }}>
                {MODULES.map((m) => {
                    const mod = modules[m] || { status: 'UNKNOWN', trend: 'unknown', summary: '' };
                    const openCount = (openByModule[m] || []).length;
                    return (
                        <Card key={m}>
                            <CardBody style={{ padding: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                                    <div style={{ fontWeight: 600, fontSize: 15, letterSpacing: 0.3 }}>{m}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Badge
                                            style={{
                                                backgroundColor: STATUS_COLOR[mod.status] || '#34495e',
                                                color: '#fff',
                                                padding: '4px 8px',
                                                fontSize: 11,
                                            }}
                                        >
                                            {mod.status.replace(/_/g, ' ')}
                                        </Badge>
                                        <span
                                            title={`Trend: ${mod.trend}`}
                                            style={{ color: TREND_COLOR[mod.trend] || '#7f8c8d', fontSize: 16, fontWeight: 600 }}
                                        >
                                            {TREND_SYMBOL[mod.trend] || '·'}
                                        </span>
                                    </div>
                                </div>
                                <div style={{ fontSize: 12, color: '#7f8c8d', marginBottom: 8 }}>
                                    {openCount} open finding{openCount === 1 ? '' : 's'}
                                </div>
                                {mod.headline ? (
                                    <div style={{ fontSize: 14, color: '#2c3e50', fontWeight: 600, lineHeight: 1.5, marginBottom: 6 }}>
                                        {reformat_prose_dates(mod.headline)}
                                    </div>
                                ) : null}
                                {mod.summary ? (
                                    <div style={{ fontSize: 13, color: '#566573', lineHeight: 1.6 }}>
                                        {reformat_prose_dates(mod.summary)}
                                    </div>
                                ) : null}
                            </CardBody>
                        </Card>
                    );
                })}
            </div>

            <div style={{ fontSize: 12, color: '#95a5a6' }}>
                Digest v{data.digest ? data.digest.version : 0} ·
                {data.digest && data.digest.lastEventAt
                    ? ' last event ' + fmt_datetime(data.digest.lastEventAt)
                    : ' no digest yet'}
            </div>
        </div>
    );
};

export default Adoption;
